/**
 * VIP Chat Page
 * 
 * Full-page VIP AI Chatbot for Elite users
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, Navigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import { iosNativeContent, iosNativePageRoot } from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MessageCircle, Send, Bot, User, Sparkles, ArrowLeft, Crown, MoreVertical, Image as ImageIcon, History, X } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { useLatestScan } from '../hooks/useLatestScan';
import { getRecommendedProductsFromScan } from '../utils/productRecommendations';
import { generateAIResponse } from '../components/VIPChatbot';
import { saveChatSession, getChatSessions, getChatSession, deleteChatSession, updateSession, createNewSession, type ChatSession, type ChatMessage } from '../services/chatHistoryService';
import { canSendChat, incrementChatCount, getRemainingChats, getChatLimit, type PlanTier } from '../utils/chatLimits';
import { useToast } from '../components/ui/Toast';

// Helper function to format markdown-style text to HTML
const formatMessage = (text: string): string => {
  // Process line by line to maintain proper list state
  const lines = text.split('\n');
  const processedLines: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' | null = null;
  let inNumberedListItem = false; // Track if we're inside a numbered list item with sub-bullets
  let inOrderedList = false; // Track if we're in an ordered list (separate from nested ul)
  let consecutiveEmptyLines = 0;
  let lastWasNumbered = false; // Track if the last non-empty line was a numbered item
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    const isLastLine = index === lines.length - 1;
    
      // Handle empty lines - they might separate list items but shouldn't break the list
      if (!trimmedLine) {
        consecutiveEmptyLines++;
        // If we're in a numbered list item, close it when we hit empty lines (but keep the ol open)
        if (inNumberedListItem && consecutiveEmptyLines >= 1) {
          if (inList && listType === 'ul') {
            processedLines.push('</ul>');
            inList = false;
            listType = null;
          }
          // Close the list item but keep the ordered list open
          processedLines.push('</li>');
          inNumberedListItem = false;
          consecutiveEmptyLines = 0;
          lastWasNumbered = true; // Mark that we just closed a numbered item
          // CRITICAL: Keep inOrderedList = true and listType = 'ol' so the next numbered item continues the same list
          if (inOrderedList) {
            inList = true;
            listType = 'ol';
          }
        } else if (lastWasNumbered && consecutiveEmptyLines <= 2) {
          // If we just had a numbered item and we're still in an ordered list context, keep it open
          // This handles cases where there are multiple empty lines between numbered items
        }
        return;
      }
    
    consecutiveEmptyLines = 0; // Reset empty line counter
    lastWasNumbered = false; // Reset numbered flag
    
    // Check if line starts with numbered list (e.g., "1. ", "2. ", or emoji numbers like "1️⃣") - use ordered list
    // Emoji numbers: 1️⃣ (U+0031 U+FE0F U+20E3), 2️⃣ (U+0032 U+FE0F U+20E3), etc.
    // Match emoji numbers (1️⃣-9️⃣) - these are composed of digit + variation selector + combining enclosing keycap
    // Also match patterns like "1. " at the start of a line
    const emojiNumberMatch = trimmedLine.match(/^[1-9]\uFE0F?\u20E3\s/);
    const regularNumberMatch = trimmedLine.match(/^\d+\.\s/);
    if (emojiNumberMatch || regularNumberMatch) {
      lastWasNumbered = true; // Mark that this is a numbered item
      // If we were in a list item with sub-bullets, close the nested ul and li first
      if (inNumberedListItem) {
        if (inList && listType === 'ul') {
          processedLines.push('</ul>');
          inList = false;
          listType = null;
          // Restore ordered list state after closing nested ul
          if (inOrderedList) {
            inList = true;
            listType = 'ol';
          }
        }
        processedLines.push('</li>');
        inNumberedListItem = false;
      }
      
      // Start or continue ordered list (use <ol> for proper numbering)
      // CRITICAL: Only create new <ol> if we're NOT already in an ol
      if (!inOrderedList) {
        // Close any other list type first
        if (inList && listType) {
          processedLines.push(`</${listType}>`);
        }
        processedLines.push('<ol>');
        inOrderedList = true;
        inList = true;
        listType = 'ol';
      } else if (listType !== 'ol') {
        // We're in an ordered list but the state got messed up, restore it
        if (inList && listType) {
          processedLines.push(`</${listType}>`);
        }
        inList = true;
        listType = 'ol';
        // Don't create a new <ol> - we're continuing the existing one
      } else if (!inList) {
        // We have inOrderedList = true but inList = false, restore it
        inList = true;
        listType = 'ol';
      }
      // If we're already in an ol, don't create a new one - just continue with the existing one
      
      // Remove number prefix (either emoji or regular number) - browser will number automatically
      const listItem = trimmedLine.replace(/^[1-9]\uFE0F?\u20E3\s/, '').replace(/^\d+\.\s/, '');
      const formattedItem = listItem.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      processedLines.push(`<li>${formattedItem}`);
      inNumberedListItem = true; // Mark that we're in a list item that might have sub-bullets
    } else if (trimmedLine.match(/^[•\-\*]\s/) && inNumberedListItem) {
      // Sub-bullet point inside a list item
      if (!inList || listType !== 'ul') {
        if (inList && listType) {
          processedLines.push(`</${listType}>`);
        }
        processedLines.push('<ul>');
        inList = true;
        listType = 'ul';
        // Note: inOrderedList remains true - we're in a nested ul inside a ul
      }
      const listItem = trimmedLine.replace(/^[•\-\*]\s/, '');
      const formattedItem = listItem.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      processedLines.push(`<li>${formattedItem}</li>`);
    } else if (trimmedLine.match(/^[•\-\*]\s/)) {
      // Standalone bullet point (not inside list)
      // Close list if we were in one
      if (inNumberedListItem) {
        if (inList && listType === 'ul') {
          processedLines.push('</ul>');
        }
        processedLines.push('</li>');
        inNumberedListItem = false;
      }
      if (inOrderedList) {
        processedLines.push('</ol>');
        inOrderedList = false;
        inList = false;
        listType = null;
      }
      
      if (!inList || listType !== 'ul') {
        if (inList && listType) {
          processedLines.push(`</${listType}>`);
        }
        processedLines.push('<ul>');
        inList = true;
        listType = 'ul';
      }
      const listItem = trimmedLine.replace(/^[•\-\*]\s/, '');
      const formattedItem = listItem.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      processedLines.push(`<li>${formattedItem}</li>`);
      } else {
      // Regular text line
      // If we were in a list item with sub-bullets, close nested structures
      if (inNumberedListItem) {
        if (inList && listType === 'ul') {
          processedLines.push('</ul>');
          inList = false;
          listType = null;
          // Restore ordered list state after closing nested ul
          if (inOrderedList) {
            inList = true;
            listType = 'ol';
          }
        }
        processedLines.push('</li>');
        inNumberedListItem = false;
      }
      
      // Close list if we were in one (and not in a list item)
      // IMPORTANT: Never close ordered lists on regular text - they might continue with more numbered items
      if (inList && listType && listType !== 'ol') {
        processedLines.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      // If we're in an ordered list, keep it open - regular text might be part of the list item or a separator
      
      // Check if line looks like a heading (ends with : and is short, or starts with **)
      if (trimmedLine.match(/^\*\*.*\*\*:$/) || (trimmedLine.endsWith(':') && trimmedLine.length < 60)) {
        const headingText = trimmedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/:$/, '');
        processedLines.push(`<h4>${headingText}</h4>`);
      } else {
        // Regular paragraph - process bold and italic
        let formattedLine = trimmedLine
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        processedLines.push(`<p>${formattedLine}</p>`);
      }
    }
  });
  
  // Close any open structures at the end
  if (inNumberedListItem) {
    if (inList && listType === 'ul') {
      processedLines.push('</ul>');
    }
    processedLines.push('</li>');
  }
  if (inOrderedList) {
    processedLines.push('</ol>');
  } else if (inList && listType) {
    processedLines.push(`</${listType}>`);
  }
  
  return processedLines.join('');
};

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 1rem 1rem 1rem;
  position: relative;
  overflow-x: hidden;

  @media (max-width: 1023px) {
    min-height: calc(100vh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
  }

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 95%;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  padding: 0 1rem;

  ${iosNativeContent}

  @media (max-width: 1023px) {
    flex: 1;
    min-height: 0;
    padding: 0;
    max-width: 100%;
  }

  @media (min-width: 1200px) {
    max-width: 1400px;
    padding-right: 340px; /* Space for Test Mode panel */
  }
`;

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const BackButton = styled(Button)`
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1<{ $isElite?: boolean }>`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${props => props.$isElite
    ? theme.colors.text.primary
    : theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  ${props => props.$isElite && `
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 30%, #92400e 60%, #78350f 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `}
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  margin: 0.5rem 0 0 0;
`;

const EliteBadge = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0.25rem;
  display: inline-block;
`;

const ChatCard = styled(Card)<{ $isElite?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  min-height: 0;
  background: ${props => props.$isElite 
    ? 'linear-gradient(135deg, #fff9e6 0%, #ffffff 100%)'
    : theme.colors.background.primary};
  border: ${props => props.$isElite 
    ? '2px solid #fbbf24'
    : `1px solid ${theme.colors.border.light}`};
  box-shadow: ${props => props.$isElite
    ? '0 10px 40px rgba(245, 158, 11, 0.2)'
    : theme.shadows.lg};
  min-height: calc(100vh - 200px);
  max-height: calc(100vh - 180px);
  height: calc(100vh - 180px);

  @media (max-width: 1023px) {
    min-height: 0;
    max-height: none;
    height: 100%;
    flex: 1;
  }
`;

const ChatHeader = styled.div<{ $isElite?: boolean }>`
  flex-shrink: 0;
  background: ${props => props.$isElite
    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%)'
    : theme.colors.primary[600]};
  color: white;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: ${props => props.$isElite
    ? '1px solid rgba(255, 255, 255, 0.2)'
    : `1px solid ${theme.colors.primary[700]}`};
`;

const ChatHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const HistoryButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.4);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChatTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VIPBadge = styled.span`
  background: rgba(255, 255, 255, 0.3);
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: ${theme.colors.background.primary};
  -webkit-overflow-scrolling: touch;

  @media (max-width: 1023px) {
    padding: 1rem;
    gap: 1rem;
    background: #ffffff;
  }
`;

const Message = styled.div<{ $isUser: boolean }>`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  flex-direction: ${props => props.$isUser ? 'row-reverse' : 'row'};
`;

const MessageAvatar = styled.div<{ $isUser: boolean; $isElite?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => props.$isUser 
    ? theme.colors.primary[600]
    : props.$isElite
      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%)'
      : theme.colors.mint[500]};
  color: white;
  box-shadow: ${props => props.$isUser 
    ? theme.shadows.md
    : props.$isElite
      ? '0 4px 12px rgba(245, 158, 11, 0.3)'
      : theme.shadows.md};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean; $isElite?: boolean }>`
  max-width: 85%;
  padding: 1.25rem 1.5rem;
  border-radius: ${theme.borderRadius.xl};
  background: ${props => props.$isUser 
    ? theme.colors.primary[600]
    : props.$isElite 
      ? 'linear-gradient(135deg, #fff9e6 0%, #ffffff 100%)'
      : theme.colors.background.secondary};
  color: ${props => props.$isUser ? 'white' : theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.8;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  border: ${props => props.$isUser 
    ? 'none'
    : props.$isElite
      ? '1px solid #fbbf24'
      : `1px solid ${theme.colors.border.light}`};
  box-shadow: ${props => props.$isUser 
    ? theme.shadows.md
    : props.$isElite
      ? '0 2px 8px rgba(245, 158, 11, 0.1)'
      : theme.shadows.sm};

  @media (max-width: 1023px) {
    max-width: 94%;
    padding: 1.35rem 1.5rem;
    font-size: ${theme.typography.fontSize.lg};
    line-height: 1.75;
  }

  /* Formatting styles for AI responses */
  h3, h4 {
    margin: 1rem 0 0.5rem 0;
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${props => props.$isUser ? 'white' : theme.colors.text.primary};
    font-size: ${theme.typography.fontSize.lg};
  }

  h4 {
    font-size: ${theme.typography.fontSize.base};
    margin-top: 0.75rem;
  }

  ul {
    margin: 0.825rem 0; /* Increased by 10% from 0.75rem */
    padding-left: 1.65rem; /* Increased by 10% from 1.5rem */
    list-style-type: disc; /* Bullet points for unordered lists */
  }
  
  /* Main list items - consistent spacing and formatting */
  ul > li {
    margin-bottom: 1.25rem !important; /* Consistent spacing between main items */
    margin-top: 0.75rem !important;
    padding-bottom: 0.5rem !important; /* Additional padding for visual separation */
    line-height: 1.7 !important;
  }
  
  /* First main list item - no top margin */
  ul > li:first-child {
    margin-top: 0 !important;
  }
  
  /* Nested ul (sub-bullets) - consistent indentation and spacing */
  ul ul {
    margin: 0.5rem 0 !important; /* Consistent spacing for nested lists */
    padding-left: 1.5rem !important; /* Consistent indentation */
    list-style-type: disc !important;
  }
  
  /* Sub-bullet items - consistent spacing */
  ul ul li {
    margin: 0.35rem 0 !important; /* Consistent spacing between sub-items */
    line-height: 1.6 !important;
    padding: 0 !important;
  }

  ol {
    margin: 0.825rem 0 !important; /* Increased by 10% from 0.75rem */
    padding-left: 2rem !important; /* Increased padding to make room for numbers */
    counter-reset: none !important; /* Prevent counter reset */
    list-style-type: decimal !important; /* Explicitly set decimal numbering */
    list-style-position: outside !important; /* Numbers outside the content */
    display: block !important;
  }
  
  ol ol {
    counter-reset: none !important; /* Prevent nested ol from resetting */
    list-style-type: decimal !important;
  }
  
  /* Ensure ol li elements display as list items with decimal numbering */
  ol li {
    display: list-item !important;
    list-style-type: decimal !important;
    margin-bottom: 1.25rem !important; /* Consistent spacing between main items */
    margin-top: 0.75rem !important;
    padding-bottom: 0.5rem !important;
    line-height: 1.7 !important;
  }
  
  /* First numbered item - no top margin */
  ol > li:first-child {
    margin-top: 0 !important;
  }
  
  /* First ol li - no top margin */
  ol li:first-child {
    margin-top: 0 !important;
  }

  /* Base li styling - will be overridden by specific selectors */
  li {
    line-height: 1.6; /* Consistent line height */
  }
  
  /* Extra spacing for nested lists inside numbered items */
  ol ul {
    margin: 0.55rem 0; /* Additional spacing for nested bullet lists */
  }
  
  ol li {
    margin-bottom: 0.825rem; /* Increased spacing between numbered items by 10% */
    display: list-item !important; /* Ensure proper list item display */
    list-style-type: decimal !important; /* Force decimal numbering */
  }
  
  /* Override any global resets that might affect ol */
  ol {
    list-style: decimal !important;
  }

  p {
    margin: 0.825rem 0; /* Increased by 10% from 0.75rem */
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ol {
    margin: 0.825rem 0; /* Increased by 10% for ordered lists */
    padding-left: 1.65rem; /* Increased by 10% from 1.5rem */
  }
  
  li {
    margin: 0.55rem 0; /* Increased by 10% from 0.5rem */
    line-height: 1.87; /* Increased by 10% from 1.7 */
  }

  strong {
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${props => props.$isUser ? 'white' : theme.colors.primary[600]};
  }

  em {
    font-style: italic;
  }
`;

const HistoryModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const HistoryModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
`;

const HistoryModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #92400e 100%);
  color: white;
`;

const HistoryModalTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const HistoryModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SessionItem = styled.div`
  padding: 1rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${theme.colors.background.secondary};
    border-color: ${theme.colors.primary[300]};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const SessionTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  font-size: ${theme.typography.fontSize.base};
`;

const SessionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const SessionDate = styled.span``;

const MessageCount = styled.span``;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: ${theme.colors.status.error}20;
  border: 1px solid ${theme.colors.status.error}40;
  color: ${theme.colors.status.error};
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.status.error}30;
    border-color: ${theme.colors.status.error}60;
  }
`;

const EmptyHistory = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${theme.colors.text.secondary};
`;

const NewChatButton = styled(Button)`
  margin-bottom: 1rem;
  width: 100%;
`;

const ChatLimitBadge = styled.div`
  padding: 0.5rem 1rem;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InputContainer = styled.div<{ $isElite?: boolean }>`
  flex-shrink: 0;
  padding: 1.5rem 2rem;
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0px));
  border-top: ${props => props.$isElite ? '2px solid #fbbf24' : `1px solid ${theme.colors.border.light}`};
  background: ${props => props.$isElite
    ? 'linear-gradient(135deg, #fff9e6 0%, #ffffff 100%)'
    : theme.colors.background.primary};
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  min-height: 0;

  @media (max-width: 1023px) {
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    background: #ffffff;
  }
`;

const ImageUploadButton = styled.button`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  min-width: 52px;
  height: 52px;

  &:hover {
    background: ${theme.colors.background.secondary};
    border-color: ${theme.colors.primary[400]};
    color: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  padding: 1rem 1.25rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  transition: all 0.2s;
  resize: none;
  min-height: 52px;
  max-height: 120px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 4px ${theme.colors.primary[100]};
  }

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const SendButton = styled(Button)<{ $isElite?: boolean }>`
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  min-width: 52px;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$isElite
    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    : theme.colors.primary[600]};
  border: none;
  box-shadow: ${props => props.$isElite
    ? '0 4px 12px rgba(245, 158, 11, 0.3)'
    : theme.shadows.md};

  &:hover:not(:disabled) {
    background: ${props => props.$isElite
      ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
      : theme.colors.primary[700]};
    box-shadow: ${props => props.$isElite
      ? '0 6px 16px rgba(245, 158, 11, 0.4)'
      : theme.shadows.lg};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TypingIndicator = styled.div<{ $isElite?: boolean }>`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem 1.25rem;
  background: ${props => props.$isElite
    ? 'linear-gradient(135deg, #fff9e6 0%, #ffffff 100%)'
    : theme.colors.background.secondary};
  border: ${props => props.$isElite
    ? '1px solid #fbbf24'
    : `1px solid ${theme.colors.border.light}`};
  border-radius: ${theme.borderRadius.lg};
  max-width: 100px;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.$isElite ? '#f59e0b' : theme.colors.primary[600]};
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const VIPChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { tier } = useSubscription();
  const { userProfile } = useAWSAuth();
  const { latestScan } = useLatestScan();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! 👋 I'm Glow Bot, your AI skin care assistant. I'm here 24/7 to help with any questions about your skin, skincare routine, products, diet, or anything else! What would you like to know?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null); // Track current conversation
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Load chat sessions when history modal opens (user-scoped)
  useEffect(() => {
    if (showHistory && userProfile?.uid) {
      setChatSessions(getChatSessions(userProfile.uid));
    }
  }, [showHistory, userProfile?.uid]);

  // Auto-save current conversation when messages change (debounced)
  // This works like ChatGPT - it saves the conversation automatically after a short delay
  useEffect(() => {
    const uid = userProfile?.uid;
    if (currentSessionId && messages.length > 1 && uid) {
      const timeoutId = setTimeout(() => {
        updateSession(currentSessionId, messages as ChatMessage[], uid);
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [messages, currentSessionId, userProfile?.uid]);

  // Auto-save when component unmounts or user navigates away
  useEffect(() => {
    const uid = userProfile?.uid;
    return () => {
      if (uid) {
        if (currentSessionId && messages.length > 1) {
          updateSession(currentSessionId, messages as ChatMessage[], uid);
        } else if (messages.length > 1 && !currentSessionId) {
          const newSessionId = createNewSession();
          saveChatSession(newSessionId, messages as ChatMessage[], uid);
        }
      }
    };
  }, [currentSessionId, messages, userProfile?.uid]);

  const handleSend = async () => {
    if (!inputValue.trim() && !uploadedImage) return;

    // Check chat limit
    if (userProfile?.uid && !canSendChat(userProfile.uid, currentTier)) {
      const upgradeTier = currentTier === 'plus' ? 'Pro' : 'Elite';
      showToast(
        `Your daily chat allowance has been reached. Please return tomorrow or upgrade to ${upgradeTier} for extended access.`,
        'error'
      );
      return;
    }

    // Increment chat count
    if (userProfile?.uid && currentTier !== 'elite') {
      incrementChatCount(userProfile.uid, currentTier);
    }

    const messageText = inputValue.trim() || (uploadedImage ? 'What do you see in this image? Please provide skincare or product-related analysis.' : '');
    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      isUser: true,
      timestamp: new Date(),
    };

    const uid = userProfile?.uid;
    const isFirstUserTurn = !currentSessionId && messages.length === 1 && !!uid;
    let sessionIdForSave = currentSessionId;
    if (isFirstUserTurn) {
      sessionIdForSave = createNewSession();
      setCurrentSessionId(sessionIdForSave);
    }

    const messagesAfterUser = [...messages, userMessage];
    setMessages(messagesAfterUser);
    setInputValue('');
    const imageToSend = uploadedImage;
    setUploadedImage(null);
    setIsTyping(true);

    if (isFirstUserTurn && sessionIdForSave && uid) {
      saveChatSession(sessionIdForSave, messagesAfterUser as ChatMessage[], uid);
    }

    // Call async AI response
    (async () => {
      const recentHistory = messagesAfterUser.slice(-10).map(m => ({
        text: m.text,
        isUser: m.isUser,
      }));

      const userGender = userProfile?.gender;
      const visibleProducts = latestScan ? getRecommendedProductsFromScan(latestScan, 20, userGender) : undefined;

      try {
        const responseText = await generateAIResponse(userMessage.text, userProfile, latestScan, recentHistory, visibleProducts, imageToSend);
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          isUser: false,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);

        if (sessionIdForSave && uid) {
          const updatedMessages = [...messagesAfterUser, aiResponse];
          updateSession(sessionIdForSave, updatedMessages as ChatMessage[], uid);
        }
      } catch (error) {
        console.error('Error generating AI response:', error);
        const errorResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'I apologize, but I encountered an error. Please try again.',
          isUser: false,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorResponse]);
        setIsTyping(false);

        if (sessionIdForSave && uid) {
          const updatedMessages = [...messagesAfterUser, errorResponse];
          updateSession(sessionIdForSave, updatedMessages as ChatMessage[], uid);
        }
      }
    })();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageDataUrl = reader.result as string;
        setUploadedImage(imageDataUrl);
        // You can add logic here to send the image to the AI or display it in the chat
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLoadSession = (sessionId: string) => {
    if (!userProfile?.uid) return;
    const session = getChatSession(sessionId, userProfile.uid);
    if (session) {
      // Save current conversation before switching (if it has messages)
      if (currentSessionId && messages.length > 1) {
        updateSession(currentSessionId, messages as ChatMessage[], userProfile.uid);
      }

      setMessages(session.messages as Message[]);
      setCurrentSessionId(sessionId);
      setShowHistory(false);
      scrollToBottom();
    }
  };

  const handleDeleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userProfile?.uid) return;
    deleteChatSession(sessionId, userProfile.uid);
    setChatSessions(getChatSessions(userProfile.uid));

    if (sessionId === currentSessionId) {
      handleNewChat();
    }
  };

  const handleNewChat = () => {
    if (!userProfile?.uid) {
      setMessages([
        {
          id: '1',
          text: "Hello! 👋 I'm Glow Bot, your AI skin care assistant. I'm here 24/7 to help with any questions about your skin, skincare routine, products, diet, or anything else! What would you like to know?",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
      setCurrentSessionId(createNewSession());
      setShowHistory(false);
      return;
    }
    // Save current conversation before starting new one (like ChatGPT)
    if (currentSessionId && messages.length > 1) {
      updateSession(currentSessionId, messages as ChatMessage[], userProfile.uid);
    } else if (messages.length > 1) {
      const newSessionId = createNewSession();
      saveChatSession(newSessionId, messages as ChatMessage[], userProfile.uid);
      setCurrentSessionId(newSessionId);
    }

    // Start fresh conversation
    const newSessionId = createNewSession();
    setMessages([
      {
        id: '1',
        text: "Hello! 👋 I'm Glow Bot, your AI skin care assistant. I'm here 24/7 to help with any questions about your skin, skincare routine, products, diet, or anything else! What would you like to know?",
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    setCurrentSessionId(newSessionId);
    setShowHistory(false);
  };

  const { showToast } = useToast();
  const currentTier = (tier || 'plus') as PlanTier;
  
  // Allow Plus, Pro, and Elite users
  if (tier !== 'plus' && tier !== 'pro' && tier !== 'elite') {
    return <Navigate to="/pricing" replace />;
  }

  // Get chat limits
  const remainingChats = userProfile?.uid 
    ? getRemainingChats(userProfile.uid, currentTier)
    : (currentTier === 'elite' ? 'unlimited' : 0);
  const chatLimit = getChatLimit(currentTier);
  const canChat = currentTier === 'elite' || (typeof remainingChats === 'number' && remainingChats > 0);

  return (
    <PageContainer>
      <Container>
        <Header>
          <BackButton variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </BackButton>
          <HeaderContent>
            <Title $isElite={currentTier === 'elite'}>
              <Bot size={32} />
              Glow Bot
            </Title>
            <Subtitle>
              Your AI-powered skin care assistant. Ask anything about your skin, products, diet, or routine!
            </Subtitle>
            <EliteBadge>
              {currentTier === 'elite' ? 'GlowLens Elite' : currentTier === 'pro' ? 'GlowLens Pro' : 'GlowLens Plus'}
            </EliteBadge>
          </HeaderContent>
        </Header>

        <ChatCard $isElite={currentTier === 'elite'}>
          <ChatHeader $isElite={currentTier === 'elite'}>
            <ChatHeaderContent>
              <Sparkles size={24} />
              <div>
                <ChatTitle>Glow Bot</ChatTitle>
                <VIPBadge>
                  {currentTier === 'elite' 
                    ? '24/7 Support' 
                    : `${chatLimit} chats/day`}
                </VIPBadge>
              </div>
            </ChatHeaderContent>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <HistoryButton onClick={() => setShowHistory(true)}>
              <History size={16} />
              View History
            </HistoryButton>
            </div>
          </ChatHeader>

          <MessagesContainer ref={messagesContainerRef}>
            {messages.map((message) => (
              <Message key={message.id} $isUser={message.isUser}>
                <MessageAvatar $isUser={message.isUser} $isElite={currentTier === 'elite'}>
                  {message.isUser ? <User /> : <Bot />}
                </MessageAvatar>
                <MessageBubble $isUser={message.isUser} $isElite={currentTier === 'elite'}>
                  {message.isUser ? (
                    message.text
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }} />
                  )}
                </MessageBubble>
              </Message>
            ))}
            {isTyping && (
              <Message $isUser={false}>
                <MessageAvatar $isUser={false} $isElite={currentTier === 'elite'}>
                  <Bot />
                </MessageAvatar>
                <TypingIndicator $isElite={currentTier === 'elite'}>
                  <span />
                  <span />
                  <span />
                </TypingIndicator>
              </Message>
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer $isElite={currentTier === 'elite'}>
            <ImageUploadButton onClick={handleImageUpload} title="Upload image">
              <MoreVertical size={20} />
            </ImageUploadButton>
            <input
              id="vip-chat-image-upload"
              name="vipChatImageUpload"
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
              aria-label="Upload image for chat"
            />
            <Input
              id="vip-chat-input"
              name="vipChatMessage"
              placeholder="Ask me anything about your skin..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleKeyPress(e as any);
                }
              }}
              rows={1}
              aria-label="Chat message"
            />
            <SendButton 
              variant="primary" 
              onClick={handleSend} 
              disabled={(!inputValue.trim() && !uploadedImage) || !canChat}
              data-send-button
            >
              <Send size={20} />
            </SendButton>
          </InputContainer>
        </ChatCard>

        {/* History Modal */}
        <HistoryModal $isOpen={showHistory} onClick={(e: React.MouseEvent) => {
          if (e.target === e.currentTarget) {
            setShowHistory(false);
          }
        }}>
          <HistoryModalContent onClick={(e?: React.MouseEvent) => e?.stopPropagation()}>
            <HistoryModalHeader>
              <HistoryModalTitle>
                <History size={24} />
                Chat History
              </HistoryModalTitle>
              <CloseButton onClick={() => setShowHistory(false)}>
                <X size={20} />
              </CloseButton>
            </HistoryModalHeader>
            <HistoryModalBody>
              <NewChatButton variant="primary" onClick={handleNewChat}>
                Start New Chat
              </NewChatButton>
              {chatSessions.length === 0 ? (
                <EmptyHistory>
                  <p>No chat history yet. Start a conversation to save it here!</p>
                </EmptyHistory>
              ) : (
                <SessionList>
                  {chatSessions.map((session) => (
                    <SessionItem
                      key={session.id}
                      onClick={() => handleLoadSession(session.id)}
                    >
                            <DeleteButton
                              onClick={(e) => handleDeleteSession(session.id, e)}
                              title="Delete chat"
                            >
                              Delete
                            </DeleteButton>
                          <SessionTitle>{session.title}</SessionTitle>
                          <SessionMeta>
                            <SessionDate>
                              {new Date(session.lastMessageAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </SessionDate>
                            <MessageCount>
                              {session.messages.length} {session.messages.length === 1 ? 'message' : 'messages'}
                            </MessageCount>
                          </SessionMeta>
                    </SessionItem>
                  ))}
                </SessionList>
              )}
            </HistoryModalBody>
          </HistoryModalContent>
        </HistoryModal>
      </Container>
    </PageContainer>
  );
};

export default VIPChatPage;

